(function () {
  'use strict';

  if (window.__marlowProductHeroInit) {
    window.__marlowProductHeroInit(document);
    return;
  }

  var controllers = new WeakMap();
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileQuery = window.matchMedia('(max-width: 767px)');

  function padNumber(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function parseJSON(element, fallback) {
    if (!element) return fallback;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return fallback;
    }
  }

  function HeroController(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-marlow-slide]'));
    this.currentIndex = Math.max(0, this.slides.findIndex(function (slide) {
      return slide.classList.contains('is-active');
    }));
    this.viewport = root.querySelector('[data-marlow-viewport]');
    this.track = root.querySelector('[data-marlow-track]');
    this.railTrack = root.querySelector('[data-marlow-rail-track]');
    this.railItems = Array.prototype.slice.call(root.querySelectorAll('[data-marlow-rail-item]'));
    this.introEnabled = root.dataset.introEnabled === 'true';
    this.introDuration = Math.max(3500, Number(root.dataset.introDuration) || 5000);
    this.introDropDuration = 860;
    this.introSpreadDuration = 780;
    this.introStagger = 110;
    this.introTimers = [];
    this.introStarted = false;
    this.introPrepareTimer = 0;
    this.introPrepareFrame = 0;
    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.transitionLocked = false;
    this.queuedIndex = null;
    this.queuedDirection = 0;
    this.requestLocked = false;
    this.touch = null;
    this.dragFrame = 0;
    this.lastSwipeAt = 0;
    this.heroVisible = true;
    this.footerVisible = false;
    this.sticky = root.querySelector('[data-marlow-sticky]');
    this.stickyButton = root.querySelector('[data-marlow-sticky-add]');
    this.previousButton = root.querySelector('[data-marlow-prev]');
    this.nextButton = root.querySelector('[data-marlow-next]');
    this.currentCounter = root.querySelector('[data-marlow-current]');

    this.bind();
    this.slides.forEach(this.prepareSlide.bind(this));
    this.activate(this.currentIndex, 0, true);
    this.observeVisibility();
    this.updateStickyVisibility();
    this.prepareIntro();
  }

  HeroController.prototype.bind = function () {
    var self = this;
    var options = { signal: this.signal };

    if (this.previousButton) {
      this.previousButton.addEventListener('click', function () {
        self.requestActivation(self.currentIndex - 1, -1);
      }, options);
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', function () {
        self.requestActivation(self.currentIndex + 1, 1);
      }, options);
    }

    this.root.addEventListener('wheel', function (event) {
      self.onWheel(event);
    }, { passive: false, signal: this.signal });

    this.root.addEventListener('keydown', function (event) {
      self.onKeydown(event);
    }, options);

    this.root.addEventListener('pointerdown', function (event) {
      self.onPointerDown(event);
    }, { passive: true, signal: this.signal });

    this.root.addEventListener('pointermove', function (event) {
      self.onPointerMove(event);
    }, { passive: false, signal: this.signal });

    this.root.addEventListener('pointerup', function (event) {
      self.onPointerUp(event);
    }, { passive: true, signal: this.signal });

    this.root.addEventListener('pointercancel', function () {
      self.onPointerCancel();
    }, { passive: true, signal: this.signal });

    this.root.addEventListener('click', function (event) {
      var railItem = event.target.closest('[data-marlow-rail-item]');
      if (railItem) {
        var railIndex = Number(railItem.dataset.index);
        self.requestActivation(railIndex, railIndex > self.currentIndex ? 1 : -1);
        self.openPurchase();
        return;
      }

      var media = event.target.closest('[data-marlow-media]');
      if (media && Date.now() - self.lastSwipeAt < 400) {
        event.preventDefault();
      }
    }, options);

    if (this.stickyButton) {
      this.stickyButton.addEventListener('click', function () {
        var slide = self.activeSlide();
        var form = slide && slide.querySelector('[data-marlow-product-form]');
        var selected = slide && self.selectedOptions(slide);
        var optionCount = slide ? Number(slide.dataset.optionCount) : 0;
        if (form && selected.filter(Boolean).length === optionCount) {
          form.requestSubmit();
          return;
        }
        if (slide) {
          slide.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
          window.setTimeout(function () { self.promptSelection(slide); }, reduceMotion.matches ? 0 : 360);
        }
      }, options);
    }

    window.addEventListener('scroll', function () {
      self.updateStickyVisibility();
    }, { passive: true, signal: this.signal });

    window.addEventListener('resize', function () {
      self.positionSlides();
      self.updateStickyVisibility();
    }, { passive: true, signal: this.signal });
  };

  HeroController.prototype.prepareSlide = function (slide) {
    var self = this;
    slide._marlowVariants = parseJSON(slide.querySelector('[data-marlow-variants]'), []);
    var form = slide.querySelector('[data-marlow-product-form]');
    if (!form) return;

    form.addEventListener('change', function (event) {
      if (!event.target.matches('.marlow-hero__option-input')) return;
      self.updateOptionAvailability(slide);
      self.updateVariantState(slide);
    }, { signal: this.signal });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      self.addToCart(slide);
    }, { signal: this.signal });

    this.updateOptionAvailability(slide);
    this.updateVariantState(slide);
  };

  HeroController.prototype.activeSlide = function () {
    return this.slides[this.currentIndex] || null;
  };

  HeroController.prototype.prepareIntro = function () {
    var self = this;
    if (!this.introEnabled || reduceMotion.matches) {
      this.startIntro();
      return;
    }

    var images = this.slides.map(function (slide) {
      return slide.querySelector('.marlow-hero__picture--front img');
    }).filter(Boolean);

    if (!images.length || typeof Promise === 'undefined') {
      this.startIntro();
      return;
    }

    var decodeTasks = images.map(function (image) {
      if (typeof image.decode === 'function') {
        return image.decode().catch(function () {});
      }
      if (image.complete) return Promise.resolve();
      return new Promise(function (resolve) {
        image.addEventListener('load', resolve, { once: true, signal: self.signal });
        image.addEventListener('error', resolve, { once: true, signal: self.signal });
      });
    });

    var timeout = new Promise(function (resolve) {
      self.introPrepareTimer = window.setTimeout(resolve, 1500);
    });

    Promise.race([Promise.all(decodeTasks), timeout]).then(function () {
      window.clearTimeout(self.introPrepareTimer);
      self.introPrepareTimer = 0;
      if (self.signal.aborted) return;
      self.introPrepareFrame = window.requestAnimationFrame(function () {
        self.introPrepareFrame = window.requestAnimationFrame(function () {
          self.introPrepareFrame = 0;
          if (!self.signal.aborted) self.startIntro();
        });
      });
    });
  };

  HeroController.prototype.startIntro = function () {
    if (this.introStarted) return;
    this.introStarted = true;
    var self = this;
    if (!this.introEnabled || reduceMotion.matches) {
      this.finishIntro();
      return;
    }

    document.body.classList.add('marlow-hero-intro-active');
    this.root.classList.add('is-intro', 'is-intro-pending');

    var dropStart = 100;
    var arrivalEnd = dropStart + this.introDropDuration + (this.maxIntroOrder || 0) * this.introStagger;
    var spreadAt = arrivalEnd + (mobileQuery.matches ? 32 : 140);
    var revealAt = spreadAt + this.introSpreadDuration + 160;
    var headerAt = Math.max(revealAt + 520, Math.round(this.introDuration * 0.84));
    this.root.style.setProperty('--marlow-mobile-spread-delay', (spreadAt - dropStart) + 'ms');

    revealAt = Math.min(revealAt, this.introDuration - 900);
    headerAt = Math.min(headerAt, this.introDuration - 360);

    this.introTimers.push(window.setTimeout(function () {
      self.root.classList.remove('is-intro-pending');
      self.root.classList.add('is-intro-arriving', 'is-intro-sequence');
    }, dropStart));

    this.introTimers.push(window.setTimeout(function () {
      self.root.classList.remove('is-intro-arriving');
      self.root.classList.add('is-intro-spread');
    }, spreadAt));

    this.introTimers.push(window.setTimeout(function () {
      self.root.classList.add('is-intro-reveal');
    }, revealAt));

    this.introTimers.push(window.setTimeout(function () {
      document.body.classList.remove('marlow-hero-intro-active');
    }, headerAt));

    this.introTimers.push(window.setTimeout(function () {
      self.finishIntro();
    }, this.introDuration));
  };

  HeroController.prototype.finishIntro = function () {
    this.introTimers.forEach(function (timer) { window.clearTimeout(timer); });
    this.introTimers = [];
    this.root.classList.remove('is-intro', 'is-intro-pending', 'is-intro-arriving', 'is-intro-spread', 'is-intro-reveal', 'is-intro-sequence');
    this.root.classList.add('is-ready', 'is-shopping');
    if (!document.querySelector('[data-marlow-product-hero].is-intro')) {
      document.body.classList.remove('marlow-hero-intro-active');
    }
    this.positionSlides();
  };

  HeroController.prototype.openPurchase = function () {
    if (!this.root.classList.contains('is-ready')) return;
    this.root.classList.add('is-shopping');
    this.positionSlides();
  };

  HeroController.prototype.galleryStep = function () {
    var width = this.root.clientWidth;
    if (mobileQuery.matches) return width * 0.58;
    return Math.min(280, Math.max(150, width * 0.19));
  };

  HeroController.prototype.positionSlides = function () {
    var self = this;
    var count = this.slides.length;
    var half = Math.floor(count / 2);
    var step = this.galleryStep();
    var introStep = mobileQuery.matches
      ? Math.min(104, step * 0.46)
      : Math.min(72, step * 0.28);
    var relativePositions = this.slides.map(function (_, slideIndex) {
      var relative = slideIndex - self.currentIndex;
      if (relative > half) relative -= count;
      if (relative < -half) relative += count;
      if (count % 2 === 0 && relative === half) relative = -half;
      return relative;
    });
    var introOrders = relativePositions.map(function (relative) {
      return relative === 0 ? 0 : (Math.abs(relative) * 2) - (relative > 0 ? 1 : 0);
    });
    var maxIntroOrder = introOrders.length ? Math.max.apply(Math, introOrders) : 0;
    this.maxIntroOrder = maxIntroOrder;

    this.slides.forEach(function (slide, slideIndex) {
      var relative = relativePositions[slideIndex];

      slide.style.setProperty('--marlow-slot-offset', relative * step + 'px');
      slide.style.setProperty('--marlow-intro-slot-offset', relative * introStep + 'px');
      var introScale = mobileQuery.matches
        ? (relative === 0 ? '0.82' : Math.abs(relative) === 1 ? '0.74' : '0.68')
        : (relative === 0 ? '1.04' : Math.abs(relative) === 1 ? '0.92' : '0.82');
      slide.style.setProperty('--marlow-intro-scale', introScale);
      var introOrder = introOrders[slideIndex];
      slide.style.setProperty('--marlow-intro-delay', introOrder * self.introStagger + 'ms');
      slide.style.setProperty(
        '--marlow-intro-drop-duration',
        self.introDropDuration + (maxIntroOrder - introOrder) * self.introStagger + 'ms'
      );
      slide.classList.toggle('is-far', Math.abs(relative) > 1);

      var media = slide.querySelector('[data-marlow-media]');
      var content = slide.querySelector('[data-marlow-content]');
      var active = slideIndex === self.currentIndex;
      slide.dataset.marlowSlot = active ? 'center' : 'side';
      self.syncVisualSlot(slide, active);
      var contentActive = active && self.root.classList.contains('is-shopping');
      if (content) {
        content.inert = !contentActive;
        content.setAttribute('aria-hidden', contentActive ? 'false' : 'true');
      }
    });

    this.railItems.forEach(function (item, itemIndex) {
      var active = itemIndex === self.currentIndex;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-current', active ? 'true' : 'false');
    });

    if (this.railTrack) {
      var railOffset = mobileQuery.matches
        ? (this.root.clientWidth * 0.26) - (this.currentIndex * this.root.clientWidth * 0.48)
        : 0;
      this.railTrack.style.setProperty('--marlow-rail-offset', railOffset + 'px');
    }
  };

  HeroController.prototype.syncVisualSlot = function (slide, active) {
    var media = slide.querySelector('[data-marlow-media]');
    var introActive = this.root.classList.contains('is-intro');
    var showBack = media && media.classList.contains('is-back');

    slide.querySelectorAll('.marlow-hero__picture--blur').forEach(function (picture) {
      var isBackPicture = picture.classList.contains('marlow-hero__picture--back');
      var showBlur = !introActive && !active && (showBack ? isBackPicture : !isBackPicture);
      picture.style.setProperty('opacity', showBlur ? '1' : '0', 'important');
      picture.style.setProperty('visibility', showBlur ? 'visible' : 'hidden', 'important');
    });

    if (active) {
      slide.querySelectorAll('.marlow-hero__picture--sharp .marlow-hero__image').forEach(function (image) {
        image.style.setProperty('filter', 'none', 'important');
      });
    }
  };

  HeroController.prototype.activate = function (index, direction, immediate) {
    var slideCount = this.slides.length;
    if (!slideCount) return false;
    index = ((index % slideCount) + slideCount) % slideCount;
    if (!immediate && index === this.currentIndex) return false;
    if (this.transitionLocked && !immediate) return false;

    var self = this;
    var previous = this.activeSlide();
    var next = this.slides[index];

    if (!immediate) {
      this.transitionLocked = true;
      this.root.classList.remove('is-moving-next', 'is-moving-previous');
      this.root.classList.add(direction > 0 ? 'is-moving-next' : 'is-moving-previous');
      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = window.setTimeout(function () {
        self.transitionLocked = false;
        self.root.classList.remove('is-moving-next', 'is-moving-previous');
        if (self.queuedIndex !== null) {
          var queuedIndex = self.queuedIndex;
          var queuedDirection = self.queuedDirection;
          self.queuedIndex = null;
          self.queuedDirection = 0;
          window.requestAnimationFrame(function () {
            self.activate(queuedIndex, queuedDirection);
          });
        }
      }, reduceMotion.matches ? 20 : (mobileQuery.matches ? 600 : 740));
    }

    if (previous && previous !== next) {
      previous.classList.remove('is-active');
    }

    this.currentIndex = index;
    next.classList.add('is-active');
    next.dataset.direction = direction > 0 ? 'next' : 'previous';

    this.slides.forEach(function (slide, slideIndex) {
      slide.classList.toggle('is-before', slideIndex < index);
      slide.classList.toggle('is-after', slideIndex > index);
    });

    if (this.track) {
      this.track.style.setProperty('--marlow-track-index', index);
    }

    this.positionSlides();

    if (!immediate) this.resetSlide(next);
    if (this.currentCounter) this.currentCounter.textContent = padNumber(index + 1);
    if (this.previousButton) this.previousButton.disabled = slideCount < 2;
    if (this.nextButton) this.nextButton.disabled = slideCount < 2;

    this.updateStickyContent();
    return true;
  };

  HeroController.prototype.requestActivation = function (index, direction) {
    var slideCount = this.slides.length;
    if (!slideCount) return false;
    var normalizedIndex = ((index % slideCount) + slideCount) % slideCount;
    if (normalizedIndex === this.currentIndex && !this.transitionLocked) return false;
    if (this.transitionLocked) {
      this.queuedIndex = normalizedIndex;
      this.queuedDirection = direction;
      return true;
    }
    return this.activate(normalizedIndex, direction);
  };

  HeroController.prototype.resetSlide = function (slide) {
    slide.querySelectorAll('.marlow-hero__option-input').forEach(function (input) {
      input.checked = false;
    });
    var media = slide.querySelector('[data-marlow-media]');
    if (media) media.classList.remove('is-back');
    var status = slide.querySelector('[data-marlow-status]');
    if (status) {
      status.textContent = '';
      status.classList.remove('is-error');
    }
    this.updateOptionAvailability(slide);
    this.updateVariantState(slide);
  };

  HeroController.prototype.onWheel = function (event) {
    if (!this.root.classList.contains('is-ready')) return;
    if (this.slides.length < 2 || Math.abs(event.deltaX) < 32 || Math.abs(event.deltaX) <= Math.abs(event.deltaY) * 1.15) return;
    var direction = event.deltaX > 0 ? 1 : -1;
    var targetIndex = this.currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= this.slides.length) return;
    if (this.activate(targetIndex, direction)) event.preventDefault();
  };

  HeroController.prototype.onKeydown = function (event) {
    if (!this.root.classList.contains('is-ready')) return;
    if (event.target.matches('input, button, a, select, textarea')) return;
    var direction = 0;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') direction = 1;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') direction = -1;
    if (!direction) return;
    if (this.requestActivation(this.currentIndex + direction, direction)) event.preventDefault();
  };

  HeroController.prototype.resolveGestureAxis = function (deltaX, deltaY) {
    var horizontal = Math.abs(deltaX);
    var vertical = Math.abs(deltaY);
    var distance = Math.max(horizontal, vertical);
    if (distance < 8) return null;
    if (horizontal >= vertical * 1.1) return 'horizontal';
    if (vertical >= horizontal * 1.1) return 'vertical';
    if (distance < 18) return null;
    return horizontal > vertical ? 'horizontal' : 'vertical';
  };

  HeroController.prototype.updateTouchOffset = function (deltaX) {
    if (!this.touch) return;
    var maxDrag = this.root.clientWidth * 0.48;
    var offset = Math.max(-maxDrag, Math.min(maxDrag, deltaX));

    if (!this.touch.direction) this.touch.direction = offset < 0 ? 1 : -1;
    offset = this.touch.direction > 0 ? Math.min(0, offset) : Math.max(0, offset);
    this.touch.offset = offset;

    if (Math.abs(offset) > Math.abs(this.touch.peakOffset)) {
      this.touch.peakOffset = offset;
    }
  };

  HeroController.prototype.swipeThreshold = function () {
    return Math.min(36, Math.max(24, this.root.clientWidth * 0.055));
  };

  HeroController.prototype.completeSwipe = function (touchState, allowVelocity) {
    var distance = Math.abs(touchState.peakOffset);
    var elapsed = Date.now() - touchState.time;
    var velocity = distance / Math.max(elapsed, 1);
    var shouldAdvance = touchState.axis === 'horizontal' && touchState.direction && (
      distance >= this.swipeThreshold() ||
      (allowVelocity && distance >= 16 && velocity >= 0.28)
    );
    var direction = touchState.direction;

    this.releasePointer();
    if (touchState.axis !== 'horizontal') return;
    this.lastSwipeAt = Date.now();

    if (shouldAdvance) {
      this.requestActivation(this.currentIndex + direction, direction);
    }
  };

  HeroController.prototype.previewDrag = function (offset) {
    var slideCount = this.slides.length;
    var direction = offset < 0 ? 1 : -1;
    var targetIndex = ((this.currentIndex + direction) % slideCount + slideCount) % slideCount;

    this.slides.forEach(function (slide, slideIndex) {
      var dragOffset = offset * -0.12;
      if (slideIndex === targetIndex) dragOffset = offset * 0.55;
      if (slideIndex === this.currentIndex) dragOffset = offset;
      slide.style.setProperty('--marlow-slide-drag', dragOffset + 'px');
    }, this);

    if (this.railTrack) this.railTrack.style.setProperty('--marlow-rail-drag', offset * 0.3 + 'px');
  };

  HeroController.prototype.clearDragPreview = function () {
    this.slides.forEach(function (slide) {
      slide.style.setProperty('--marlow-slide-drag', '0px');
    });
    if (this.railTrack) this.railTrack.style.setProperty('--marlow-rail-drag', '0px');
  };

  HeroController.prototype.onPointerDown = function (event) {
    if (!this.root.classList.contains('is-ready') || !event.isPrimary || event.button !== 0 || !event.target.closest('[data-marlow-media]')) {
      this.touch = null;
      return;
    }
    this.touch = {
      pointerId: event.pointerId,
      target: event.target,
      x: event.clientX,
      y: event.clientY,
      currentX: event.clientX,
      currentY: event.clientY,
      time: Date.now(),
      axis: null,
      offset: 0,
      peakOffset: 0,
      direction: 0,
      suppressDrag: this.transitionLocked
    };
  };

  HeroController.prototype.onPointerMove = function (event) {
    if (!this.touch || event.pointerId !== this.touch.pointerId || !this.track) return;
    var deltaX = event.clientX - this.touch.x;
    var deltaY = event.clientY - this.touch.y;

    this.touch.currentX = event.clientX;
    this.touch.currentY = event.clientY;

    if (!this.touch.axis) {
      this.touch.axis = this.resolveGestureAxis(deltaX, deltaY);
      if (!this.touch.axis) return;
      if (this.touch.axis === 'vertical') {
        this.releasePointer();
        return;
      }
      this.touch.direction = deltaX < 0 ? 1 : -1;
      if (this.touch.target && this.touch.target.setPointerCapture) {
        this.touch.target.setPointerCapture(this.touch.pointerId);
      }
    }

    if (this.touch.axis !== 'horizontal') return;
    event.preventDefault();

    this.updateTouchOffset(deltaX);
    if (this.touch.suppressDrag) return;
    this.track.classList.add('is-dragging');
    this.root.classList.add('is-dragging');
    if (this.viewport) this.viewport.classList.add('is-dragging');
    if (!this.dragFrame) {
      var self = this;
      this.dragFrame = window.requestAnimationFrame(function () {
        self.dragFrame = 0;
        if (!self.touch) return;
        self.previewDrag(self.touch.offset);
      });
    }
  };

  HeroController.prototype.onPointerUp = function (event) {
    if (!this.touch || event.pointerId !== this.touch.pointerId) return;
    var touchState = this.touch;
    var deltaX = event.clientX - touchState.x;
    var deltaY = event.clientY - touchState.y;
    if (!touchState.axis) touchState.axis = this.resolveGestureAxis(deltaX, deltaY);
    if (touchState.axis === 'horizontal') {
      if (!touchState.direction) touchState.direction = deltaX < 0 ? 1 : -1;
      this.updateTouchOffset(deltaX);
    }

    this.completeSwipe(touchState, true);
  };

  HeroController.prototype.onPointerCancel = function () {
    if (!this.touch) {
      this.releasePointer();
      return;
    }
    this.completeSwipe(this.touch, false);
  };

  HeroController.prototype.releasePointer = function () {
    if (this.dragFrame) {
      window.cancelAnimationFrame(this.dragFrame);
      this.dragFrame = 0;
    }
    if (this.touch && this.touch.target && this.touch.target.hasPointerCapture && this.touch.target.hasPointerCapture(this.touch.pointerId)) {
      this.touch.target.releasePointerCapture(this.touch.pointerId);
    }
    this.touch = null;
    if (this.track) {
      this.track.classList.remove('is-dragging');
    }
    this.root.classList.remove('is-dragging');
    this.clearDragPreview();
    if (this.viewport) this.viewport.classList.remove('is-dragging');
  };

  HeroController.prototype.selectedOptions = function (slide) {
    var optionCount = Number(slide.dataset.optionCount) || 0;
    var selected = new Array(optionCount).fill(null);
    slide.querySelectorAll('.marlow-hero__option-input:checked').forEach(function (input) {
      selected[Number(input.dataset.optionIndex)] = input.value;
    });
    return selected;
  };

  HeroController.prototype.findVariant = function (slide, selected) {
    return (slide._marlowVariants || []).find(function (variant) {
      return selected.every(function (value, index) {
        return value !== null && variant.options[index] === value;
      });
    }) || null;
  };

  HeroController.prototype.updateOptionAvailability = function (slide) {
    var variants = slide._marlowVariants || [];
    var selected = this.selectedOptions(slide);
    var inputs = Array.prototype.slice.call(slide.querySelectorAll('.marlow-hero__option-input'));

    function applyAvailability(currentSelection) {
      var clearedSelection = false;
      inputs.forEach(function (input) {
        var candidateIndex = Number(input.dataset.optionIndex);
        var candidateValue = input.value;
        var isAvailable = variants.some(function (variant) {
          if (!variant.available || variant.options[candidateIndex] !== candidateValue) return false;
          return currentSelection.every(function (value, selectedIndex) {
            if (selectedIndex === candidateIndex || value === null) return true;
            return variant.options[selectedIndex] === value;
          });
        });

        if (!isAvailable && input.checked) {
          input.checked = false;
          clearedSelection = true;
        }
        input.disabled = !isAvailable;
        input.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
        input.closest('.marlow-hero__option-label').classList.toggle('is-unavailable', !isAvailable);
      });
      return clearedSelection;
    }

    if (applyAvailability(selected)) applyAvailability(this.selectedOptions(slide));
  };

  HeroController.prototype.formatMoney = function (cents) {
    var format = this.root.dataset.moneyFormat || '{{ amount }}';
    var value = Number(cents || 0) / 100;
    function withSeparators(number, decimals, thousands, decimal) {
      var parts = Math.abs(number).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return decimals ? parts.join(decimal) : parts[0];
    }
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, withSeparators(value, 2, ',', '.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withSeparators(value, 0, ',', '.'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withSeparators(value, 2, '.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withSeparators(value, 0, '.', ','));
  };

  HeroController.prototype.updateVariantState = function (slide) {
    var optionCount = Number(slide.dataset.optionCount) || 0;
    var selected = this.selectedOptions(slide);
    var complete = optionCount === 0 || selected.every(Boolean);
    var variant = optionCount === 0 ? (slide._marlowVariants || [])[0] : this.findVariant(slide, selected);
    var addButton = slide.querySelector('[data-marlow-add]');
    var label = slide.querySelector('[data-marlow-add-label]');
    var variantInput = slide.querySelector('[data-marlow-variant-id]');
    var price = slide.querySelector('[data-marlow-price]');
    var productAvailable = slide.dataset.productAvailable === 'true';

    slide._marlowSelectedVariant = complete ? variant : null;
    if (variantInput) variantInput.value = complete && variant ? variant.id : '';

    if (price) {
      var displayPrice = complete && variant ? variant.price : Number(slide.dataset.basePrice);
      var displayCompare = complete && variant ? variant.compare_at_price : Number(slide.dataset.baseComparePrice);
      price.innerHTML = '';
      if (displayCompare && displayCompare > displayPrice) {
        var compare = document.createElement('s');
        compare.textContent = this.formatMoney(displayCompare);
        price.appendChild(compare);
      }
      var current = document.createElement('span');
      current.textContent = this.formatMoney(displayPrice);
      price.appendChild(current);
    }

    if (addButton && label) {
      if (!productAvailable) {
        addButton.disabled = true;
        label.textContent = addButton.dataset.soldOutLabel;
      } else if (!complete) {
        addButton.disabled = false;
        label.textContent = addButton.dataset.selectLabel;
      } else if (!variant || !variant.available) {
        addButton.disabled = true;
        label.textContent = addButton.dataset.soldOutLabel;
      } else {
        addButton.disabled = false;
        label.textContent = addButton.dataset.addLabel + ' — ' + this.formatMoney(variant.price);
      }
    }

    this.updateStickyContent();
  };

  HeroController.prototype.promptSelection = function (slide) {
    var status = slide.querySelector('[data-marlow-status]');
    if (status) {
      status.textContent = this.root.dataset.sizeError;
      status.classList.add('is-error');
    }
    var sizeIndex = Number(slide.dataset.sizeOptionIndex);
    var selector = sizeIndex >= 0
      ? '.marlow-hero__option-input[data-option-index="' + sizeIndex + '"]:not(:disabled)'
      : '.marlow-hero__option-input:not(:disabled)';
    var firstInput = slide.querySelector(selector);
    if (firstInput) firstInput.focus({ preventScroll: true });
  };

  HeroController.prototype.setLoading = function (slide, loading) {
    var button = slide.querySelector('[data-marlow-add]');
    if (!button) return;
    var label = button.querySelector('[data-marlow-add-label]');
    button.classList.toggle('is-loading', loading);
    button.disabled = loading;
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (loading && label) label.textContent = button.dataset.loadingLabel;
  };

  HeroController.prototype.addToCart = function (slide) {
    var self = this;
    if (this.requestLocked) return;
    var optionCount = Number(slide.dataset.optionCount) || 0;
    var selected = this.selectedOptions(slide);
    var complete = optionCount === 0 || selected.every(Boolean);
    var variant = slide._marlowSelectedVariant;
    var status = slide.querySelector('[data-marlow-status]');
    var button = slide.querySelector('[data-marlow-add]');
    var succeeded = false;

    if (!complete) {
      this.promptSelection(slide);
      return;
    }
    if (!variant || !variant.available || (button && button.disabled)) return;

    this.requestLocked = true;
    this.setLoading(slide, true);
    if (status) {
      status.textContent = '';
      status.classList.remove('is-error');
    }

    fetch(this.root.dataset.cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: [{ id: Number(variant.id), quantity: 1 }],
        sections: 'marlow-cart-drawer',
        sections_url: window.location.pathname
      })
    })
      .then(function (response) {
        return response.json().then(function (payload) {
          if (!response.ok) {
            throw new Error(payload.description || payload.message || self.root.dataset.cartError);
          }
          return payload;
        });
      })
      .then(function (payload) {
        succeeded = true;
        self.setLoading(slide, false);
        document.dispatchEvent(new CustomEvent('marlow:cart-updated', {
          detail: { payload: payload, sections: payload.sections || {}, open: true, trigger: button }
        }));
        if (status) status.textContent = self.root.dataset.addedLabel;
        if (button) button.querySelector('[data-marlow-add-label]').textContent = self.root.dataset.addedLabel;
        window.setTimeout(function () {
          self.updateVariantState(slide);
          if (status) status.textContent = '';
        }, 1800);
      })
      .catch(function (error) {
        self.setLoading(slide, false);
        self.updateVariantState(slide);
        if (status) {
          status.textContent = error.message || self.root.dataset.cartError;
          status.classList.add('is-error');
        }
      })
      .finally(function () {
        self.requestLocked = false;
        if (!succeeded) self.setLoading(slide, false);
      });
  };

  HeroController.prototype.observeVisibility = function () {
    var self = this;
    if ('IntersectionObserver' in window) {
      this.heroObserver = new IntersectionObserver(function (entries) {
        self.heroVisible = entries[0] ? entries[0].isIntersecting : true;
      }, { threshold: 0.08 });
      this.heroObserver.observe(this.root);

      var footer = document.querySelector('footer, [role="contentinfo"]');
      if (footer) {
        this.footerObserver = new IntersectionObserver(function (entries) {
          self.footerVisible = entries[0] ? entries[0].isIntersecting : false;
          self.updateStickyVisibility();
        }, { threshold: 0.05 });
        this.footerObserver.observe(footer);
      }
    }
  };

  HeroController.prototype.updateStickyContent = function () {
    if (!this.sticky) return;
    var slide = this.activeSlide();
    if (!slide) return;
    var title = this.sticky.querySelector('[data-marlow-sticky-title]');
    var price = this.sticky.querySelector('[data-marlow-sticky-price]');
    var size = this.sticky.querySelector('[data-marlow-sticky-size]');
    var add = this.sticky.querySelector('[data-marlow-sticky-add]');
    var selectedVariant = slide._marlowSelectedVariant;
    var sizeIndex = Number(slide.dataset.sizeOptionIndex);
    var sizeInput = sizeIndex >= 0
      ? slide.querySelector('.marlow-hero__option-input[data-option-index="' + sizeIndex + '"]:checked')
      : null;
    var heroAdd = slide.querySelector('[data-marlow-add]');

    if (title) title.textContent = slide.dataset.productTitle;
    if (price) price.textContent = this.formatMoney(selectedVariant ? selectedVariant.price : slide.dataset.basePrice);
    if (size) size.textContent = sizeInput ? sizeInput.value : '—';
    if (add && heroAdd) {
      add.textContent = heroAdd.querySelector('[data-marlow-add-label]').textContent;
      add.disabled = heroAdd.disabled && slide.dataset.productAvailable !== 'true';
    }
  };

  HeroController.prototype.updateStickyVisibility = function () {
    if (!this.sticky) return;
    var enabled = this.root.dataset.stickyEnabled === 'true';
    var passedHero = this.root.getBoundingClientRect().bottom <= 1;
    var show = enabled && mobileQuery.matches && passedHero && !this.footerVisible;
    this.sticky.classList.toggle('is-visible', show);
    this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
  };

  HeroController.prototype.destroy = function () {
    this.abortController.abort();
    window.clearTimeout(this.transitionTimer);
    window.clearTimeout(this.introPrepareTimer);
    if (this.introPrepareFrame) window.cancelAnimationFrame(this.introPrepareFrame);
    if (this.dragFrame) window.cancelAnimationFrame(this.dragFrame);
    this.queuedIndex = null;
    this.introTimers.forEach(function (timer) { window.clearTimeout(timer); });
    this.root.classList.remove('is-intro', 'is-intro-pending', 'is-intro-arriving', 'is-intro-spread', 'is-intro-reveal', 'is-intro-sequence');
    if (!document.querySelector('[data-marlow-product-hero].is-intro')) {
      document.body.classList.remove('marlow-hero-intro-active');
    }
    if (this.heroObserver) this.heroObserver.disconnect();
    if (this.footerObserver) this.footerObserver.disconnect();
    controllers.delete(this.root);
  };

  function init(scope) {
    var roots = scope.matches && scope.matches('[data-marlow-product-hero]')
      ? [scope]
      : Array.prototype.slice.call(scope.querySelectorAll('[data-marlow-product-hero]'));
    roots.forEach(function (root) {
      if (!controllers.has(root)) controllers.set(root, new HeroController(root));
    });
  }

  window.__marlowProductHeroInit = init;
  init(document);

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-marlow-product-hero]');
    if (root && controllers.has(root)) controllers.get(root).destroy();
  });

  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target.closest('[data-marlow-slide]');
    var root = slide && slide.closest('[data-marlow-product-hero]');
    if (!slide || !root || !controllers.has(root)) return;
    controllers.get(root).finishIntro();
    controllers.get(root).activate(Number(slide.dataset.index), 0, true);
  });
})();
